import {IconChevronDown, IconSearch} from "@tabler/icons-react";
import {Dropdown, Input, Space} from "@ui";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {LinkButton} from "@/components/Button";

import IconSettings from "../../assets/images/icon-settings.svg";
import useGetUserWallet from "../../hooks/wallet/useGetUserWallet";
import useCurrencyStore from "../../store/useCurrency.store";
import {formatCurrencyAmount} from "../../util/balance.util";
import styles from "./CurrencyDropdown.module.css";
import CurrencyManagementModal from "./CurrencyManagementModal";
import CustomSwitch from "./CustomSwitch";

const CurrencyDropdown = () => {
  const {t} = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [filterFiatOnly, setFilterFiatOnly] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // Helper function to normalize text by removing accents and converting to lowercase
  const normalizeText = text => {
    return text
      .toLowerCase()
      .normalize("NFD") // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, ""); // Remove accent marks
  };

  const {
    currencies,
    //selectedCurrency,
    setSelectedCurrency,
    showManagementModal,
  } = useCurrencyStore();

  const {getAccountByCurrency} = useGetUserWallet();

  // Show all currencies by default, filter by enabled status and search/fiat filter
  const filteredCurrencies = currencies.filter(currency => {
    if (!currency.enabled) {
      return false;
    }

    const translatedName = t(`currency${currency.id}`);
    const translatedSymbol = t(`currencySymbol${currency.id}`);

    // Normalize both search term and currency data for accent-insensitive search
    const normalizedSearch = normalizeText(searchValue);
    const normalizedName = normalizeText(translatedName);
    const normalizedSymbol = normalizeText(translatedSymbol);

    const matchesSearch = normalizedName.includes(normalizedSearch) || normalizedSymbol.includes(normalizedSearch);

    if (filterFiatOnly) {
      return matchesSearch && currency.type === "fiat";
    }

    return matchesSearch;
  });

  const handleCurrencySelect = currencyId => {
    setSelectedCurrency(currencyId);
    setDropdownVisible(false);
    setSearchValue("");
  };

  const handleManageClick = () => {
    setDropdownVisible(false);
    showManagementModal();
  };

  const dropdownMenu = {
    items: [
      {
        key: "search",
        label: (
          <div
            className={styles.searchContainer}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{flex: 1, display: "flex", alignItems: "center"}}>
                <Input
                  placeholder={t("currencySearch")}
                  suffix={
                    <IconSearch
                      size={16}
                      stroke={2}
                    />
                  }
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  className={styles.searchInput}
                  style={{
                    width: "100%",
                    height: "35px",
                    borderRadius: "28px",
                    transform: "rotate(0deg)",
                    opacity: 1,
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />
              </div>
              <div>
                <LinkButton
                  icon={
                    <img
                      src={IconSettings}
                      alt="settings"
                      style={{width: 20, height: 20}}
                    />
                  }
                  onClick={handleManageClick}
                  className={styles.manageIconButton}
                  title={t("currencyManage")}
                />
              </div>
            </div>
          </div>
        ),
      },

      // Fiat section title
      {
        key: "fiat-title",
        label: (
          <div
            style={{
              padding: "8px 12px",
              paddingBottom: "4px",
              color: "var(--action-primary)",
              fontWeight: "600",
              fontSize: "16px",
              marginBottom: "4px",
            }}
          >
            {t("currencyName")}
          </div>
        ),
      },
      // Fiat currencies
      ...filteredCurrencies
        .filter(c => c.type === "fiat")
        .map(currency => {
          const account = getAccountByCurrency(currency.id);

          // Use the project's currency formatting utility instead of manual formatting
          const formattedBalance = formatCurrencyAmount(
            account?.balance || 0,
            currency.id,
            t(`currencySymbol${currency.id}`)
          );

          return {
            key: currency.id,
            label: (
              <div
                className={styles.currencyItem}
                onClick={() => handleCurrencySelect(currency.id)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCurrencySelect(currency.id);
                  }
                }}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div className={styles.currencyLeft}>
                  <span className={styles.currencyName}>{t(`currency${currency.id}`)}</span>
                </div>
                <div className={styles.currencyBalance}>{formattedBalance}</div>
              </div>
            ),
          };
        }),
      // Crypto currencies (only show if not filtering fiat only)
      ...(!filterFiatOnly
        ? [
            // Crypto section title
            {
              key: "crypto-title",
              label: (
                <div
                  style={{
                    padding: "8px 12px",
                    paddingBottom: "4px",
                    color: "var(--action-primary)",
                    fontWeight: "600",
                    fontSize: "16px",
                    marginBottom: "4px",
                  }}
                >
                  {t("cryptoNames")}
                </div>
              ),
            },
            ...filteredCurrencies
              .filter(c => c.type !== "fiat")
              .map(currency => {
                const account = getAccountByCurrency(currency.id);

                // Use the project's currency formatting utility instead of manual formatting
                const formattedBalance = formatCurrencyAmount(
                  account?.balance || 0,
                  currency.id,
                  t(`currencySymbol${currency.id}`)
                );

                return {
                  key: currency.id,
                  label: (
                    <div
                      className={styles.currencyItem}
                      onClick={() => handleCurrencySelect(currency.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleCurrencySelect(currency.id);
                        }
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div className={styles.currencyLeft}>
                        <span className={styles.currencyName}>{t(`currency${currency.id}`)}</span>
                      </div>
                      <div className={styles.currencyBalance}>{formattedBalance}</div>
                    </div>
                  ),
                };
              }),
          ]
        : []),
      {
        type: "divider",
        style: {
          width: "250px",
          height: "0px",
          transform: "rotate(0deg)",
          opacity: 1,
          borderWidth: "1px",
          border: "1px solid var(--theme-border-medium)",
          margin: "8px auto",
          display: "block",
          alignSelf: "center",
        },
      },
      {
        key: "filter",
        label: (
          <div
            className={styles.filterContainer}
            onClick={e => e.stopPropagation()}
          >
            <Space>
              <span className={styles.fiat}>{t("currencyFiatOnly")}</span>
              <CustomSwitch
                checked={filterFiatOnly}
                onChange={setFilterFiatOnly}
                size="small"
              />
            </Space>
          </div>
        ),
      },
    ],
  };

  // const formatSelectedCurrency = () => {
  //   if (!selectedCurrency) {
  //     return "";
  //   }

  //   const symbol = t(`currencySymbol${selectedCurrency.id}`);
  //   // Format the symbol consistently with balance formatting utility
  //   // For VES, ensure it has a space after "Bs." to match configCurrencyVE
  //   if (selectedCurrency.id === "VES" && symbol === "Bs.") {
  //     return "Bs. ";
  //   }
  //   return symbol;
  // };

  return (
    <>
      <Dropdown
        menu={dropdownMenu}
        trigger={["click"]}
        open={dropdownVisible}
        onOpenChange={setDropdownVisible}
        overlayClassName={styles.dropdownOverlay}
        autoAdjustOverflow={true}
      >
        <button
          className={styles.currencySelector}
          size="small"
          type="button"
        >
          <Space>
            <IconChevronDown
              size={16}
              stroke={2}
              style={{
                color: "var(--text-secondary)",
                transition: "transform 0.2s ease",
                transform: dropdownVisible ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Space>
        </button>
      </Dropdown>
      <CurrencyManagementModal />
    </>
  );
};

export default CurrencyDropdown;
